import { useState, useEffect, useRef } from 'react';
import { User, Upload, Loader2, Building2, AlertCircle } from 'lucide-react';
import { FloatingSaveBar } from './FloatingSaveBar';
import CustomSelect from '../shared/CustomSelect';
import { getUserProfile, updateUserProfile, uploadProfilePicture, uploadCompanyLogo } from '../../services/settingsService';
import { useProfile } from '../../contexts/ProfileContext';
import { useAuth } from '../../contexts/AuthContext';
import { canEditCompanyInfo } from '../../utils/permissions';
import type { UserProfile } from '../../types/settings';

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France',
  'Spain', 'Italy', 'Netherlands', 'Belgium', 'Switzerland', 'Sweden', 'Norway',
  'Denmark', 'Finland', 'Austria', 'Ireland', 'Poland', 'Portugal', 'Greece',
  'United Arab Emirates', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman',
  'India', 'China', 'Japan', 'Singapore', 'South Korea', 'Hong Kong', 'Malaysia',
  'Thailand', 'Indonesia', 'Philippines', 'Vietnam', 'New Zealand', 'South Africa',
  'Brazil', 'Mexico', 'Argentina', 'Chile', 'Colombia', 'Peru'
];

const INDUSTRIES = [
  'Technology', 'Software', 'SaaS', 'E-commerce', 'Retail', 'Finance', 'Banking',
  'Insurance', 'Healthcare', 'Pharmaceuticals', 'Manufacturing', 'Construction',
  'Real Estate', 'Education', 'Consulting', 'Marketing', 'Advertising', 'Media',
  'Entertainment', 'Hospitality', 'Travel', 'Transportation', 'Logistics',
  'Energy', 'Utilities', 'Telecommunications', 'Automotive', 'Aerospace',
  'Agriculture', 'Food & Beverage', 'Professional Services', 'Legal', 'Other'
];

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris',
  'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome', 'Europe/Amsterdam',
  'Europe/Brussels', 'Europe/Vienna', 'Europe/Zurich', 'Europe/Stockholm',
  'Europe/Oslo', 'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Dublin',
  'Europe/Warsaw', 'Europe/Prague', 'Europe/Budapest', 'Europe/Athens',
  'Asia/Dubai', 'Asia/Riyadh', 'Asia/Kuwait', 'Asia/Bahrain', 'Asia/Muscat',
  'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Singapore', 'Asia/Seoul',
  'Asia/Hong_Kong', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Manila',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland', 'UTC'
];

const LANGUAGES = [
  'English'
];

export function ProfileSettings() {
  const { user } = useAuth();
  const canEditCompany = canEditCompanyInfo(user);
  const { refreshProfile } = useProfile();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [originalProfile, setOriginalProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | undefined>();
  const [hasChanges, setHasChanges] = useState(false);
  const profilePictureInputRef = useRef<HTMLInputElement>(null);
  const companyLogoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (profile && originalProfile) {
      const changed = JSON.stringify(profile) !== JSON.stringify(originalProfile);
      setHasChanges(changed);
    }
  }, [profile, originalProfile]);

  const loadProfile = async () => {
    setLoading(true);
    const data = await getUserProfile();
    setProfile(data);
    setOriginalProfile(JSON.parse(JSON.stringify(data)));
    setLoading(false);
  };

  const handleSave = async () => {
    if (!profile) return;

    setSaving(true);
    const success = await updateUserProfile(profile);

    if (success) {
      setSaved(true);
      setLastSaved(new Date());
      setOriginalProfile(JSON.parse(JSON.stringify(profile)));
      setHasChanges(false);
      await refreshProfile();
      setTimeout(() => setSaved(false), 2000);
    }

    setSaving(false);
  };

  const handleDiscard = () => {
    if (originalProfile) {
      setProfile(JSON.parse(JSON.stringify(originalProfile)));
      setHasChanges(false);
    }
  };

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    const url = await uploadProfilePicture(file);

    if (url && profile) {
      setProfile({ ...profile, profile_picture_url: url });
      await updateUserProfile({ profile_picture_url: url });
      await refreshProfile();
    } else {
      alert('Failed to upload profile picture. Please try again.');
    }

    setUploading(false);
  };

  const handleCompanyLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    const url = await uploadCompanyLogo(file);

    if (url && profile) {
      setProfile({ ...profile, company_logo_url: url });
      await updateUserProfile({ company_logo_url: url });
      console.log('Company logo uploaded successfully:', url);
    } else {
      console.error('Failed to upload company logo');
      alert('Failed to upload company logo. Please try again.');
    }

    setUploadingLogo(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load profile</p>
      </div>
    );
  }

  const countryOptions = COUNTRIES.map(c => ({ value: c, label: c }));
  const industryOptions = INDUSTRIES.map(i => ({ value: i, label: i }));
  const timezoneOptions = TIMEZONES.map(tz => ({ value: tz, label: tz.replace(/_/g, ' ') }));
  const languageOptions = LANGUAGES.map(l => ({ value: l, label: l }));

  return (
    <div className={`max-w-full ${hasChanges || saving || saved ? 'pb-28' : ''}`}>
      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
          <User className="w-6 h-6 text-[#1875FF]" />
        </div>
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Profile Settings</h2>
          <p className="text-gray-600">Manage your personal and company information</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className={`bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5 ${!canEditCompany ? 'pointer-events-none opacity-75' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-extrabold text-gray-900 font-display">Company Info</h3>
            {!canEditCompany && (
              <span className="flex items-center gap-1.5 text-xs text-blue-700 font-medium bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200">
                <AlertCircle className="w-3 h-3" />
                View only
              </span>
            )}
          </div>

          <div className="flex items-start gap-4 mb-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center overflow-hidden border-2 border-gray-200">
                {profile.company_logo_url ? (
                  <img
                    src={profile.company_logo_url}
                    alt="Company Logo"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Failed to load company logo:', profile.company_logo_url);
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent && !parent.querySelector('.fallback-icon')) {
                        const icon = document.createElement('div');
                        icon.className = 'fallback-icon';
                        icon.innerHTML = '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-blue-500"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>';
                        parent.appendChild(icon);
                      }
                    }}
                  />
                ) : (
                  <Building2 className="w-10 h-10 text-[#1875FF]" />
                )}
              </div>
              <button
                onClick={() => companyLogoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="absolute bottom-0 right-0 p-1.5 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {uploadingLogo ? (
                  <Loader2 className="w-4 h-4 text-gray-600 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 text-gray-600" />
                )}
              </button>
              <input
                ref={companyLogoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleCompanyLogoUpload}
                className="hidden"
              />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900 mb-1">Company Logo</h4>
              <p className="text-xs text-gray-500 mb-2">JPG, PNG. Max 5MB.</p>
              <button
                onClick={() => companyLogoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="text-xs text-[#1875FF] hover:text-[#05E0D1] font-medium disabled:opacity-50"
              >
                {uploadingLogo ? 'Uploading...' : 'Upload logo'}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Company Name
              </label>
              <input
                type="text"
                value={profile.company_name || ''}
                onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                placeholder="Acme Inc."
                className="w-full px-5 py-2 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 text-base font-medium"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Company Website
              </label>
              <input
                type="url"
                value={profile.company_website || ''}
                onChange={(e) => setProfile({ ...profile, company_website: e.target.value })}
                placeholder="https://example.com"
                className="w-full px-5 py-2 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 text-base font-medium"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Country
              </label>
              <CustomSelect
                value={profile.country || ''}
                onChange={(value) => setProfile({ ...profile, country: value })}
                options={countryOptions}
                placeholder="Select country"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Company Industry
              </label>
              <CustomSelect
                value={profile.company_industry || ''}
                onChange={(value) => setProfile({ ...profile, company_industry: value })}
                options={industryOptions}
                placeholder="Select industry"
              />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
          <h3 className="text-lg font-extrabold text-gray-900 font-display mb-4">Contact Info</h3>

          <div className="flex items-start gap-4 mb-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center overflow-hidden border-2 border-gray-200">
                {profile.profile_picture_url ? (
                  <img
                    src={profile.profile_picture_url}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Failed to load profile picture:', profile.profile_picture_url);
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent && !parent.querySelector('.fallback-icon')) {
                        const icon = document.createElement('div');
                        icon.className = 'fallback-icon';
                        icon.innerHTML = '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-blue-500"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
                        parent.appendChild(icon);
                      }
                    }}
                  />
                ) : (
                  <User className="w-10 h-10 text-[#1875FF]" />
                )}
              </div>
              <button
                onClick={() => profilePictureInputRef.current?.click()}
                disabled={uploading}
                className="absolute bottom-0 right-0 p-1.5 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 text-gray-600 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 text-gray-600" />
                )}
              </button>
              <input
                ref={profilePictureInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleProfilePictureUpload}
                className="hidden"
              />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900 mb-1">Profile Picture</h4>
              <p className="text-xs text-gray-500 mb-2">JPG, PNG. Max 5MB.</p>
              <button
                onClick={() => profilePictureInputRef.current?.click()}
                disabled={uploading}
                className="text-xs text-[#1875FF] hover:text-[#05E0D1] font-medium disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload picture'}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Full Name
              </label>
              <input
                type="text"
                value={profile.full_name || ''}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                placeholder="John Doe"
                className="w-full px-5 py-2 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 text-base font-medium"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Job Title
              </label>
              <input
                type="text"
                value={profile.job_title || ''}
                onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
                placeholder="Sales Manager"
                className="w-full px-5 py-2 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 text-base font-medium"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Phone Number
              </label>
              <input
                type="tel"
                value={profile.phone_number || ''}
                onChange={(e) => setProfile({ ...profile, phone_number: e.target.value })}
                placeholder="+1 (555) 000-0000"
                className="w-full px-5 py-2 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 text-base font-medium"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={profile.email_address || ''}
                onChange={(e) => setProfile({ ...profile, email_address: e.target.value })}
                placeholder="john@example.com"
                className="w-full px-5 py-2 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 text-base font-medium"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5 mb-6">
        <h3 className="text-lg font-extrabold text-gray-900 font-display mb-4">User Preferences</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Time Zone
            </label>
            <CustomSelect
              value={profile.time_zone || ''}
              onChange={(value) => setProfile({ ...profile, time_zone: value })}
              options={timezoneOptions}
              placeholder="Select timezone"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Language
            </label>
            <CustomSelect
              value={profile.language || ''}
              onChange={(value) => setProfile({ ...profile, language: value })}
              options={languageOptions}
              placeholder="Select language"
            />
          </div>
        </div>
      </div>

      <FloatingSaveBar
        hasChanges={hasChanges}
        saving={saving}
        saved={saved}
        lastSaved={lastSaved}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />
    </div>
  );
}
